import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import './PayPalModal.css';

const PayPalModal = ({ isOpen, onClose, onPurchase, t }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const paypalRef = useRef();

  // Package configurations - Updated pricing (better value with larger packages)
  const packages = [
    {
      id: 'plan1',
      points: 100,
      price: 1.25,
      pointsPerDollar: 80,
      popular: false,
      badge: t?.('starter') || 'Starter',
      description: t?.('perfectForTryingOut') || 'Perfect for trying out'
    },
    {
      id: 'plan2',
      points: 425,
      price: 5,
      pointsPerDollar: 85,
      popular: false,
      badge: t?.('basic') || 'Basic',
      description: t?.('bestValueForBeginners') || 'Best value for beginners'
    },
    {
      id: 'plan3',
      points: 880,
      price: 10,
      pointsPerDollar: 88,
      popular: true,
      badge: t?.('mostPopular') || 'Popular',
      description: t?.('seriousPlayersChoice') || 'Serious players choice'
    },
    {
      id: 'plan4',
      points: 2250,
      price: 25,
      pointsPerDollar: 90,
      popular: false,
      badge: t?.('greatValue') || 'Value',
      description: t?.('tournamentReady') || 'Tournament ready'
    },
    {
      id: 'plan5',
      points: 4600,
      price: 50,
      pointsPerDollar: 92,
      popular: false,
      badge: t?.('premium') || 'Premium',
      description: t?.('competitiveEdge') || 'Competitive edge'
    },
    {
      id: 'plan6',
      points: 9500,
      price: 100,
      pointsPerDollar: 95,
      popular: false,
      badge: t?.('ultimate') || 'Ultimate',
      description: t?.('proTournamentBundle') || 'Pro tournament bundle'
    },
    {
      id: 'plan7',
      points: 15000,
      price: 150,
      pointsPerDollar: 100,
      popular: false,
      badge: t?.('megaPack') || 'Mega Pack',
      description: t?.('maximumValuePack') || 'Maximum value - Best deal!'
    }
  ];

  // Load PayPal SDK
  useEffect(() => {
    if (isOpen && !paypalLoaded) {
      loadPayPalSDK();
    }
  }, [isOpen, paypalLoaded]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPackage(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Render PayPal buttons when package is selected
  useEffect(() => {
    if (selectedPackage && paypalLoaded && window.paypal && paypalRef.current) {
      renderPayPalButtons();
    }
  }, [selectedPackage, paypalLoaded]);

  const loadPayPalSDK = () => {
    if (window.paypal) {
      setPaypalLoaded(true);
      return;
    }

    const script = document.createElement('script');
    // Updated URL with logging disabled and additional parameters
    script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.REACT_APP_PAYPAL_CLIENT_ID}&currency=USD&intent=capture&enable-funding=venmo,card&disable-funding=credit,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort&components=buttons`;
    script.async = true;
    script.onload = () => {
      setPaypalLoaded(true);
    };
    script.onerror = () => {
      toast.error('Failed to load PayPal SDK');
    };
    
    // Add script attributes to reduce tracking/logging
    script.setAttribute('data-csp-nonce', '');
    script.setAttribute('data-log-level', 'error');
    
    document.body.appendChild(script);
  };

  const renderPayPalButtons = () => {
    if (!paypalRef.current || !window.paypal) return;

    // Clear existing buttons
    paypalRef.current.innerHTML = '';

    window.paypal.Buttons({
      createOrder: async (data, actions) => {
        try {
          setLoading(true);
          
          // Create order on backend
          const response = await axios.post('/api/payments/create-order', {
            packageId: selectedPackage.id,
            points: selectedPackage.points,
            amount: selectedPackage.price
          });

          return response.data.orderID;
        } catch (error) {
          console.error('Create order error:', error);
          toast.error('Failed to create PayPal order');
          setLoading(false);
          throw error;
        }
      },

      onApprove: async (data, actions) => {
        try {
          setLoading(true);
          
          // Capture payment on backend
          const response = await axios.post('/api/payments/capture-order', {
            orderID: data.orderID
          });

          if (response.data.message) {
            toast.success(response.data.message);
            
            // Call the onPurchase callback
            if (onPurchase) {
              await onPurchase({
                ...selectedPackage,
                orderID: data.orderID,
                newBalance: response.data.newBalance
              });
            }
            
            onClose();
          }
        } catch (error) {
          console.error('Capture payment error:', error);
          toast.error(error.response?.data?.message || 'Payment failed');
        } finally {
          setLoading(false);
        }
      },

      onError: (error) => {
        console.error('PayPal error:', error);
        toast.error('PayPal payment error occurred');
        setLoading(false);
      },

      onCancel: () => {
        toast.info('Payment cancelled');
        setLoading(false);
      },

      style: {
        layout: 'vertical',
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 45,
        tagline: false
      }
    }).render(paypalRef.current).catch(err => {
      console.error('PayPal render error:', err);
      toast.error('Failed to render PayPal buttons');
    });
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
  };

  if (!isOpen) return null;

  // Use Portal to render modal at body level (escapes stacking contexts)
  return ReactDOM.createPortal(
    <div className="paypal-modal-overlay">
      <div className="paypal-modal">
        {/* Header */}
        <div className="paypal-modal-header">
          <button
            onClick={onClose}
            className="paypal-close-button"
            disabled={loading}
          >
            ✕
          </button>
          
          <div className="paypal-header-content">
            <div className="paypal-header-icon">
              💰
            </div>
            <div className="paypal-header-text">
              <h2>{t?.('buyTournamentPoints') || 'Buy Tournament Points'}</h2>
              <p>{t?.('chooseYourPerfectPackage') || 'Choose your perfect package and start competing!'}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="paypal-modal-content">
          {/* Package Selection */}
          <div className="package-selection-header">
            <h3 className="package-selection-title">
              📦 {t?.('selectYourPackage') || 'Select Your Package'}
            </h3>
            <p className="package-selection-description">
              {t?.('chooseTheAmountOfPoints') || 'Choose the amount of points that fits your gaming needs'}
            </p>
          </div>

          <div className="packages-grid">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
                onClick={() => handlePackageSelect(pkg)}
              >
                {selectedPackage?.id === pkg.id && (
                  <div className="selection-indicator">
                    ✓
                  </div>
                )}
                
                {pkg.popular && (
                  <div className="package-badge popular">
                    ⭐ {t?.('mostPopular') || 'Most Popular'}
                  </div>
                )}
                
                <div className="package-icon">
                  <div className={`package-icon-wrapper ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}>
                    💎
                  </div>
                </div>

                <div className="package-points">
                  <div className="package-points-number">
                    {pkg.points.toLocaleString()}
                  </div>
                  <div className="package-points-label">
                    {t?.('points') || 'Points'}
                  </div>
                </div>

                <div className="package-price">
                  <div className="package-price-number">
                    ${pkg.price.toFixed(2)}
                  </div>
                  <div className="package-price-label">
                    {Math.round(pkg.points / pkg.price)} {t?.('pointsPerDollar') || 'points per $'}
                  </div>
                </div>

                <p className="package-description">
                  {pkg.description}
                </p>

                <div className="package-features">
                  <div className="package-feature">
                    <div className="feature-dot"></div>
                    {t?.('instantDelivery') || 'Instant delivery'}
                  </div>
                  <div className="package-feature">
                    <div className="feature-dot"></div>
                    {t?.('securePayment') || 'Secure payment'}
                  </div>
                  <div className="package-feature">
                    <div className="feature-dot"></div>
                    {t?.('noExpiration') || 'No expiration'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Selected Package Summary */}
          {selectedPackage && (
            <div className="selected-summary">
              <div className="selected-summary-left">
                <div className="selected-summary-icon">
                  ✅
                </div>
                <div className="selected-summary-text">
                  <h4>{selectedPackage.points.toLocaleString()} {t?.('points') || 'Points'}</h4>
                  <p>{selectedPackage.description}</p>
                </div>
              </div>
              <div className="selected-summary-price">
                <div className="selected-summary-amount">
                  ${selectedPackage.price.toFixed(2)}
                </div>
                <div className="selected-summary-rate">
                  {Math.round(selectedPackage.points / selectedPackage.price)} {t?.('pointsPerDollar') || 'points per $'}
                </div>
              </div>
            </div>
          )}

          {/* PayPal Payment */}
          {selectedPackage && (
            <div className="paypal-container">
              {loading && (
                <div className="payment-loading">
                  <div className="loading-spinner"></div>
                  <span className="payment-loading-text">
                    {t?.('processingPayment') || 'Processing Payment...'}
                  </span>
                </div>
              )}
              <div ref={paypalRef} style={{ opacity: loading ? 0.5 : 1 }}></div>
            </div>
          )}

          {/* Payment Information */}
          <div className="payment-section">
            <div className="payment-info">
              <div className="payment-info-icon">
                🔒
              </div>
              <div className="payment-info-text">
                <h4>{t?.('securePaymentInfo') || 'Secure Payment Information'}</h4>
                <div className="payment-info-list">
                  <div>• {t?.('yourPaymentProcessedSecurely') || 'Your payment is processed securely by PayPal'}</div>
                  <div>• {t?.('pointsAddedInstantly') || 'Points are added to your account instantly'}</div>
                  <div>• {t?.('allTransactionsEncrypted') || 'All transactions are encrypted and secure'}</div>
                  <div>• {t?.('noHiddenFeesOrCharges') || 'No hidden fees or charges'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions">
            <h3 className="instructions-title">
              📋 {t?.('howToPurchase') || 'How to Purchase'}
            </h3>
            <div className="instructions-grid">
              <div className="instruction-step">
                <div className="step-number">1</div>
                <div className="instruction-content">
                  <p className="instruction-title">{t?.('selectPackage') || 'Select Package'}</p>
                  <p className="instruction-description">
                    {t?.('chooseYourPreferredPackage') || 'Choose your preferred points package above'}
                  </p>
                </div>
              </div>
              
              <div className="instruction-step">
                <div className="step-number">2</div>
                <div className="instruction-content">
                  <p className="instruction-title">{t?.('clickPayPal') || 'Click PayPal'}</p>
                  <p className="instruction-description">
                    {t?.('proceedWithSecureCheckout') || 'Proceed with secure PayPal checkout'}
                  </p>
                </div>
              </div>
              
              <div className="instruction-step">
                <div className="step-number">3</div>
                <div className="instruction-content">
                  <p className="instruction-title">{t?.('completePayment') || 'Complete Payment'}</p>
                  <p className="instruction-description">
                    {t?.('finishTransactionViaPayPal') || 'Finish transaction via PayPal'}
                  </p>
                </div>
              </div>
              
              <div className="instruction-step">
                <div className="step-number final">4</div>
                <div className="instruction-content">
                  <p className="instruction-title">{t?.('instantDelivery') || 'Instant Delivery'}</p>
                  <p className="instruction-description">
                    {t?.('pointsAddedImmediatelyToAccount') || 'Points added immediately to your account'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PayPalModal;